// public/v3/src/components/qmb-pill.jsx — patient-level QMB verification pill.
// Click to open an inline form (date + verification method); save writes
// data.qmb = { confirmed: true, source: 'manual', checkedAt, date, method } to the patient blob
// via ccmApi.updatePatient. Once set, persists across months — used by the
// APCM tier heuristic to elevate ≥2-chronic patients from G0557 to G0558.

import React, { useState, useEffect, useRef } from 'react';
import { ccmApi } from '../lib/api';

export function QmbPill({ patient, onSaved }) {
  const raw = patient?._raw || patient || {};
  const qmb = raw.qmb || null;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(qmb?.date || new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState(qmb?.method || 'HETS');
  const [error, setError] = useState(null);
  const [flipUp, setFlipUp] = useState(false);
  const popRef = useRef(null);
  const triggerRef = useRef(null);

  // Reset form state when the popover opens or when the underlying patient
  // blob changes — useState retains stale local values across re-opens.
  useEffect(() => {
    if (!open) return;
    setDate(qmb?.date || new Date().toISOString().slice(0, 10));
    setMethod(qmb?.method || 'HETS');
    setError(null);
  }, [open, qmb?.date, qmb?.method]);

  // Click-outside-to-close. popRef is on the outer span so clicks on the
  // trigger itself don't fire close-then-open.
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Escape-to-close.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open]);

  // Flip the popover upward when there's not enough room below.
  useEffect(() => {
    if (!open) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // 240px is a generous estimate for popover height (form + buttons).
    setFlipUp(rect.bottom + 240 > window.innerHeight);
  }, [open]);

  // save/clear preserve _updatedAt on the body so the server's 409 optimistic-
  // lock fires on stale writes (server/routes/patients.js:117-194). The
  // response's new updatedAt is merged into nextRaw before onSaved fires so
  // the parent's local state cache carries the fresh timestamp.
  async function save() {
    setBusy(true); setError(null);
    try {
      const checkedAt = date ? `${date}T00:00:00.000Z` : new Date().toISOString();
      const expiresAt = new Date(new Date(checkedAt).getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();
      const next = {
        ...raw,
        qmb: { confirmed: true, source: 'manual', checkedAt, expiresAt, date, method },
      };
      const resp = await ccmApi.updatePatient(patient.id, next);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      onSaved?.(fresh ? { ...next, _updatedAt: fresh } : next);
      setOpen(false);
    } catch (e) {
      if (e && e.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
      } else {
        setError(e?.message || 'Save failed');
      }
    } finally { setBusy(false); }
  }

  async function clear() {
    if (!window.confirm('Clear QMB verification? The patient will revert to the dual-eligibility heuristic until re-verified.')) return;
    setBusy(true); setError(null);
    try {
      const { qmb: _q, ...rest } = raw;
      const resp = await ccmApi.updatePatient(patient.id, rest);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      onSaved?.(fresh ? { ...rest, _updatedAt: fresh } : rest);
      setOpen(false);
    } catch (e) {
      if (e && e.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
      } else {
        setError(e?.message || 'Clear failed');
      }
    } finally { setBusy(false); }
  }

  if (qmb?.confirmed) {
    const shownDate = qmb.date || (qmb.checkedAt ? String(qmb.checkedAt).slice(0, 10) : '');
    const shownMethod = qmb.method || (qmb.source === 'manual' ? 'HETS/MAC' : qmb.source || 'HETS/MAC');
    return (
      <span ref={popRef} className="apcm-pill ok" title={`QMB verified ${shownDate} via ${shownMethod}`}>
        <button
          ref={triggerRef}
          className="apcm-pill-btn"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label="QMB verified — open verification details"
          onClick={() => setOpen(o => !o)}>
          QMB ✓ <span className="tabnum">{shownDate}</span>
        </button>
        {open && (
          <div className={`apcm-pill-pop ${flipUp ? 'up' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>Verified {shownDate} via {shownMethod}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button className="btn ghost sm" onClick={clear} disabled={busy}>Clear</button>
              <button className="btn sm" onClick={() => setOpen(false)} disabled={busy}>Done</button>
            </div>
            {error && <div style={{ color: 'var(--bad)', fontSize: 12, marginTop: 6 }}>{error}</div>}
          </div>
        )}
      </span>
    );
  }

  return (
    <span ref={popRef} className="apcm-pill">
      <button
        ref={triggerRef}
        className="apcm-pill-btn"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Mark QMB verified"
        onClick={() => setOpen(o => !o)}>
        Mark QMB ▢
      </button>
      {open && (
        <div className={`apcm-pill-pop ${flipUp ? 'up' : ''}`} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 6 }}>
            Verified date + method
          </div>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
            style={{ width: '100%', padding: 6, border: '1px solid var(--line)', borderRadius: 4 }}
          />
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            disabled={busy}
            style={{ width: '100%', padding: 6, marginTop: 6, border: '1px solid var(--line)', borderRadius: 4 }}>
            <option value="HETS">HETS</option>
            <option value="MAC portal">MAC portal</option>
            <option value="QMB card">QMB card</option>
            <option value="Other">Other</option>
          </select>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
            <button className="btn ghost sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
            <button className="btn primary sm" onClick={save} disabled={busy || !date}>Save</button>
          </div>
          {error && <div style={{ color: 'var(--bad)', fontSize: 12, marginTop: 6 }}>{error}</div>}
        </div>
      )}
    </span>
  );
}
